/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import { ArrayExtensions, ObjectExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import { IProductSpecificationViewProps } from '@msdyn365-commerce-modules/product-specification/src/modules/product-specification/./product-specification';
import { renderProductBadge } from '../../common/utilities/productListExtensionHelpers';
import { canUseDOM } from '../../common/utilities/helpers';
import { IHeiProductSpecificationProps } from './hei-product-specification.props.autogenerated';
import { IHeiProductSpecificationData } from './hei-product-specification.data';

type IHeiProductSpecificationViewProps = IHeiProductSpecificationProps<IHeiProductSpecificationData> & IProductSpecificationViewProps;

const ProductSpecificationView: React.FC<IHeiProductSpecificationViewProps> = props => {
    const {
        ProductSpecification,
        ProductSpecificationTableProps,
        ProductSpecificationTableBodyProps,
        productSpecificationResult,
        data: { productSpecificationData }
    } = props;
    const rowdata =
        productSpecificationResult &&
        ArrayExtensions.hasElements(productSpecificationResult) &&
        productSpecificationResult.filter(row => !ObjectExtensions.isNullOrUndefined(row));

    const [badgeImageAdded, setBadgeImageAdded] = React.useState<boolean>(false);
    const injectBadge = React.useCallback(() => {
        if (badgeImageAdded) {
            return;
        }

        const allAttributes = productSpecificationData.result
            ?.map(attrGroup => {
                return attrGroup.Attributes;
            })
            .flat();

        if (allAttributes) {
            // process.env.NODE_ENV === 'development' && console.log('iterateAttributes', allAttributes);
            allAttributes.forEach(psr => {
                if (psr && psr.Name === 'Product Badge Status') {
                    const badgeStatus = psr.TextValue || '';
                    // process.env.NODE_ENV === 'development' && console.log('iterateAttributes', badgeStatus);
                    if (canUseDOM() && badgeStatus.toLowerCase() !== 'none') {
                        if (renderProductBadge(props.context.request, badgeStatus, '_PDP')) {
                            // process.env.NODE_ENV === 'development' && console.log('iterateAttributes', badgeStatus, 'added');
                            setBadgeImageAdded(true);
                        }
                    }
                }
            });
        }
    }, [productSpecificationData.result, badgeImageAdded]);

    injectBadge();

    return rowdata && ArrayExtensions.hasElements(rowdata) ? (
        <Module {...ProductSpecification}>
            {props.title}
            <Node {...ProductSpecificationTableProps}>
                <Node {...ProductSpecificationTableBodyProps}>{rowdata}</Node>
            </Node>
        </Module>
    ) : null;
};

export default ProductSpecificationView;
